import { useEffect } from "react";
import styled from "styled-components";
import { colors } from "../colors";
import icon from "../assets/skipgive-icon.png";
import { Image } from "../components/Reusable";
import { useViewport } from "../hooks/useViewport";

const Container = styled.div<{ $isDesktop: boolean }>`
  font-family: "Helvetica Neue", sans-serif;
  margin: 1.25rem;
  margin: 0 auto;
  padding: ${(props) => (props.$isDesktop ? "3rem 12rem" : "3rem 2rem")};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.black};
`;

const SubTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 500;
  color: ${colors.black};
  margin-top: 1.25rem;
  text-transform: uppercase;
`;

const Paragraph = styled.p`
  font-size: 1.25rem;
  color: ${colors.black};
`;

const Strong = styled.strong`
  font-weight: bold;
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 1.25rem;
`;

const ListItem = styled.li`
  color: ${colors.black};
  font-size: 1.15rem;
`;

export const TermsOfService = () => {
  const { isDesktop } = useViewport();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container $isDesktop={isDesktop}>
      <Image src={icon} />
      <Title>TERMS OF SERVICE</Title>
      <Paragraph>Effective Date: October 23, 2025</Paragraph>

      <SubTitle>1. ACCEPTANCE OF TERMS</SubTitle>
      <Paragraph>
        Welcome to SkipGive ("SkipGive," "Company," "we," "us," or "our"). By
        accessing, browsing, or using SkipGive's technology platform, website,
        services, or any related applications (collectively, the "Platform"),
        you ("User," "you," or "your") acknowledge that you have read,
        understood, and agree to be legally bound by these Terms of Service
        (these "Terms"), as well as our Privacy Policy and SMS Text Policy,
        which are incorporated herein by reference. If you do not agree to these
        Terms in their entirety, you are not authorized to use the Platform in
        any manner or form whatsoever.
      </Paragraph>

      <SubTitle>2. USE OF THE PLATFORM</SubTitle>
      <Paragraph>
        <Strong>2.1 License Grant:</Strong> Subject to your compliance with
        these Terms, SkipGive grants you a limited, non-exclusive, revocable,
        non-transferable, non-sublicensable license to access and use the
        Platform solely for your personal or internal business purposes in
        accordance with these Terms. This license does not include any right to:
        (a) resell or make commercial use of the Platform or its content; (b)
        collect and use any product listings, descriptions, or prices; (c) make
        derivative use of the Platform or its content; (d) download or copy
        account information for the benefit of any third party; or (e) use any
        data mining, robots, or similar data gathering or extraction methods.
      </Paragraph>
      <Paragraph>
        <Strong>2.2 User Conduct and Restrictions:</Strong> You agree that you
        will not, and will not permit any third party to:
      </Paragraph>
      <List>
        <ListItem>
          Use the Platform for any illegal, unlawful, unauthorized, fraudulent,
          or unethical purpose, or in violation of any applicable local, state,
          national, or international law or regulation;
        </ListItem>
        <ListItem>
          Interfere with, disrupt, or create an undue burden on the Platform or
          the networks or services connected to the Platform;
        </ListItem>
        <ListItem>
          Modify, adapt, translate, reverse engineer, decompile, disassemble, or
          otherwise attempt to discover the source code or underlying algorithms
          of the Platform;
        </ListItem>
        <ListItem>
          Transmit any viruses, worms, defects, Trojan horses, malware, or any
          items of a destructive nature;
        </ListItem>
        <ListItem>
          Attempt to gain unauthorized access to the Platform, user accounts,
          computer systems, or networks connected to the Platform;
        </ListItem>
        <ListItem>
          Use any automated system, including but not limited to robots,
          spiders, or offline readers, to access the Platform;
        </ListItem>
        <ListItem>
          Impersonate any person or entity or falsely state or otherwise
          misrepresent your affiliation with any person or entity;
        </ListItem>
        <ListItem>
          Violate the privacy rights, publicity rights, intellectual property
          rights, or any other legal rights of others; or
        </ListItem>
        <ListItem>
          Collect or store personal data about other users without their express
          consent.
        </ListItem>
      </List>
      <Paragraph>
        <Strong>2.3 Account Security:</Strong> You are responsible for
        maintaining the confidentiality of your account credentials and are
        fully responsible for all activities that occur under your account. You
        agree to immediately notify SkipGive of any unauthorized use of your
        account or any other breach of security. SkipGive will not be liable for
        any loss or damage arising from your failure to comply with this
        security obligation.
      </Paragraph>

      <SubTitle>3. INTELLECTUAL PROPERTY RIGHTS</SubTitle>
      <Paragraph>
        <Strong>3.1 Platform Ownership:</Strong> The Platform and all content,
        features, functionality, information, software, products, services,
        trademarks, service marks, trade names, logos, and other materials
        (collectively, "Platform Content") are owned by SkipGive, its licensors,
        or other providers of such material and are protected by United States
        and international copyright, trademark, patent, trade secret, and other
        intellectual property or proprietary rights laws. These Terms permit you
        to use the Platform solely for your personal, non-commercial use, or
        internal business purposes. You must not reproduce, distribute, modify,
        create derivative works of, publicly display, publicly perform,
        republish, download, store, or transmit any of the material on the
        Platform, except as incidentally necessary for your authorized use of the
        Platform.
      </Paragraph>
      <Paragraph>
        <Strong>3.2 User Content License Grant:</Strong> You retain all
        ownership rights in content that you submit, post, upload, or otherwise
        make available on or through the Platform ("User Content"). However, by
        submitting User Content to the Platform, you hereby grant SkipGive a
        worldwide, perpetual, irrevocable, non-exclusive, royalty-free, fully
        paid-up, transferable, and sublicensable right and license to use,
        reproduce, modify, adapt, publish, translate, create derivative works
        from, distribute, publicly perform, and publicly display such User
        Content (in whole or in part) in any format or medium now known or later
        developed, and for any purpose, including but not limited to promoting
        and redistributing part or all of the Platform.
      </Paragraph>
      <Paragraph>
        <Strong>3.3 User Content Representations:</Strong> You represent and
        warrant that: (a) you either are the sole and exclusive owner of all User
        Content or you have all rights, licenses, consents, and releases
        necessary to grant SkipGive the license to the User Content as set forth
        above; and (b) neither the User Content nor your submission, uploading,
        publishing, or otherwise making available of such User Content, nor
        SkipGive's use of the User Content as permitted herein, will infringe,
        misappropriate, or violate a third party's intellectual property or
        proprietary rights, or rights of publicity or privacy, or result in the
        violation of any applicable law or regulation.
      </Paragraph>
      <Paragraph>
        <Strong>3.4 Feedback:</Strong> If you provide SkipGive with any
        suggestions, ideas, enhancement requests, feedback, recommendations, or
        other information relating to the Platform ("Feedback"), you hereby
        assign to SkipGive all right, title, and interest in and to such
        Feedback, and SkipGive shall be free to use, disclose, reproduce,
        license, or otherwise distribute and exploit the Feedback as it sees fit,
        entirely without obligation or restriction of any kind.
      </Paragraph>

      <SubTitle>4. PRIVACY AND DATA PROTECTION</SubTitle>
      <Paragraph>
        <Strong>4.1 Privacy Policy:</Strong> Your use of the Platform is also
        governed by our Privacy Policy, which is incorporated into these Terms by
        reference. Please review our Privacy Policy carefully to understand our
        practices regarding your personal information and how we will treat it.
        By using the Platform, you consent to the collection, use, and disclosure
        of your information as described in our Privacy Policy.
      </Paragraph>
      <Paragraph>
        <Strong>4.2 Data Protection Compliance:</Strong> SkipGive processes
        personal information in accordance with applicable data protection laws,
        including but not limited to the California Consumer Privacy Act (CCPA),
        the General Data Protection Regulation (GDPR) where applicable to
        European users, and other relevant data protection and privacy laws. You
        acknowledge that SkipGive may transfer, store, and process your data in
        jurisdictions other than where you reside, and that such jurisdictions
        may have different data protection laws.
      </Paragraph>
      <Paragraph>
        <Strong>4.3 User Data Responsibilities:</Strong> If you collect, use, or
        process personal information of third parties through the Platform, you
        are solely responsible for complying with all applicable privacy and data
        protection laws. You represent and warrant that you have obtained all
        necessary consents and authorizations to provide such information to
        SkipGive and to use the Platform as intended.
      </Paragraph>

      <SubTitle>5. DISCLAIMER OF WARRANTIES</SubTitle>
      <Paragraph>
        YOU EXPRESSLY UNDERSTAND AND AGREE THAT YOUR USE OF THE PLATFORM IS AT
        YOUR SOLE RISK. THE PLATFORM, INCLUDING ALL CONTENT, FUNCTIONS, AND
        MATERIALS, IS PROVIDED ON AN "AS IS," "AS AVAILABLE," AND "WITH ALL
        FAULTS" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR
        IMPLIED. TO THE FULLEST EXTENT PERMISSIBLE PURSUANT TO APPLICABLE LAW,
        SKIPGIVE, ITS AFFILIATES, LICENSORS, SERVICE PROVIDERS, CONTENT
        PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, AND DIRECTORS (COLLECTIVELY,
        "SKIPGIVE PARTIES") DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
        STATUTORY OR OTHERWISE, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
        OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
        NON-INFRINGEMENT, AND THOSE ARISING FROM A COURSE OF DEALING OR USAGE OF
        TRADE.
      </Paragraph>
      <Paragraph>
        WITHOUT LIMITING THE FOREGOING, SKIPGIVE PARTIES DO NOT WARRANT THAT: (A)
        THE PLATFORM WILL MEET YOUR REQUIREMENTS OR EXPECTATIONS; (B) THE
        PLATFORM WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; (C) THE
        RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE PLATFORM WILL BE
        ACCURATE OR RELIABLE; (D) THE QUALITY OF ANY PRODUCTS, SERVICES,
        INFORMATION, OR OTHER MATERIAL OBTAINED BY YOU THROUGH THE PLATFORM WILL
        MEET YOUR EXPECTATIONS; OR (E) ANY ERRORS IN THE PLATFORM WILL BE
        CORRECTED. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY
        YOU FROM SKIPGIVE OR THROUGH THE PLATFORM SHALL CREATE ANY WARRANTY NOT
        EXPRESSLY STATED IN THESE TERMS.
      </Paragraph>
      <Paragraph>
        SKIPGIVE DOES NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY
        FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH
        THE PLATFORM, AND SKIPGIVE WILL NOT BE A PARTY TO OR IN ANY WAY BE
        RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND THIRD-PARTY
        PROVIDERS OF PRODUCTS OR SERVICES.
      </Paragraph>

      <SubTitle>6. LIMITATION OF LIABILITY</SubTitle>
      <Paragraph>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
        SKIPGIVE PARTIES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
        REVENUES, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES (EVEN IF
        SKIPGIVE HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES), WHETHER
        BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY
        OTHER LEGAL THEORY, AND WHETHER OR NOT SKIPGIVE HAS BEEN INFORMED OF THE
        POSSIBILITY OF SUCH DAMAGE, RESULTING FROM:
      </Paragraph>
      <List>
        <ListItem>
          YOUR ACCESS TO, USE OF, OR INABILITY TO ACCESS OR USE THE PLATFORM;
        </ListItem>
        <ListItem>
          ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON OR THROUGH THE PLATFORM,
          INCLUDING WITHOUT LIMITATION, ANY DEFAMATORY, OFFENSIVE, OR ILLEGAL
          CONDUCT OF OTHER USERS OR THIRD PARTIES;
        </ListItem>
        <ListItem>
          ANY CONTENT OBTAINED FROM OR THROUGH THE PLATFORM; OR
        </ListItem>
        <ListItem>
          UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR
          CONTENT.
        </ListItem>
      </List>
      <Paragraph>
        IN NO EVENT SHALL THE AGGREGATE LIABILITY OF SKIPGIVE PARTIES EXCEED THE
        GREATER OF ONE HUNDRED U.S. DOLLARS ($100.00) OR THE AMOUNT YOU PAID, IF
        ANY, TO SKIPGIVE FOR THE PLATFORM DURING THE TWELVE (12) MONTHS
        IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE LIABILITY. THE
        LIMITATIONS OF THIS SECTION SHALL APPLY TO ANY THEORY OF LIABILITY,
        WHETHER BASED ON WARRANTY, CONTRACT, STATUTE, TORT (INCLUDING
        NEGLIGENCE), OR OTHERWISE, AND WHETHER OR NOT SKIPGIVE PARTIES HAVE BEEN
        INFORMED OF THE POSSIBILITY OF ANY SUCH DAMAGE, AND EVEN IF A REMEDY SET
        FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
      </Paragraph>
      <Paragraph>
        SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN
        WARRANTIES OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR
        CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE LIMITATIONS SET FORTH
        ABOVE MAY NOT APPLY TO YOU. IF YOU ARE DISSATISFIED WITH ANY PORTION OF
        THE PLATFORM OR WITH THESE TERMS, YOUR SOLE AND EXCLUSIVE REMEDY IS TO
        DISCONTINUE USE OF THE PLATFORM.
      </Paragraph>

      <SubTitle>7. INDEMNIFICATION</SubTitle>
      <Paragraph>
        You agree to defend, indemnify, and hold harmless SkipGive Parties from
        and against any and all claims, damages, obligations, losses,
        liabilities, costs, debts, and expenses (including but not limited to
        attorneys' fees and costs) arising from or relating to: (a) your use of
        and access to the Platform; (b) your violation of any term of these
        Terms; (c) your violation of any third-party right, including without
        limitation any copyright, trademark, trade secret, or other property, or
        privacy right; (d) any claim that your User Content caused damage to a
        third party; (e) your violation of any applicable law, rule, or
        regulation; or (f) any negligent acts, omissions, or willful misconduct
        by you. This indemnification obligation will survive the termination of
        these Terms and your use of the Platform. SkipGive reserves the right, at
        your expense, to assume the exclusive defense and control of any matter
        for which you are required to indemnify SkipGive, and you agree to
        cooperate with SkipGive's defense of these claims. You will not settle
        any claim that affects SkipGive or binds SkipGive in any way without
        SkipGive's prior written consent.
      </Paragraph>

      <SubTitle>8. COMMUNITY GUIDELINES AND PROHIBITED USE</SubTitle>
      <Paragraph>
        You are solely responsible for compliance with all applicable laws in
        relation to your use of the Services. You are further solely responsible
        for all User-Generated Content that you upload, post, publish, display,
        transmit, or otherwise use (hereinafter, "Upload").
      </Paragraph>
      <Paragraph>
        SkipGive does not permit the use of its Platform for any purpose or
        campaign that involves:
      </Paragraph>
      <List>
        <ListItem>
          <Strong>Breaking the law:</Strong> the violation of any law or
          regulation.
        </ListItem>
        <ListItem>
          <Strong>Fraud:</Strong> information that is fraudulent, misleading,
          inaccurate, dishonest, or impossible, particularly if done knowingly
          with the intent to deceive.
        </ListItem>
        <ListItem>
          <Strong>Illegal drugs:</Strong> narcotics, steroids, controlled
          substances, abuse of pharmaceuticals or similar products or therapies
          that are either illegal or prohibited.
        </ListItem>
        <ListItem>
          <Strong>Violence:</Strong> knives, explosives, ammunition, firearms,
          or other weaponry or accessories, as well as the promotion of violent
          ideas such as genocide, execution, and other violent acts.
        </ListItem>
        <ListItem>
          <Strong>Financial schemes:</Strong> annuities, investments, loans,
          equity or lottery contracts, lay-away systems, off-shore banking or
          similar transactions, money service businesses (including currency
          exchanges, check cashing or the like), pyramid schemes, “get rich
          quick schemes” (i.e., investment opportunities or other services that
          promise high rewards), network marketing and referral marketing
          programs, debt collection, or cryptocurrencies.
        </ListItem>
        <ListItem>
          <Strong>Gambling:</Strong> casino games, sports betting, fantasy
          sports, horse or greyhound racing, lottery tickets, and other ventures
          that facilitate gambling, games of skill or chance (whether or not, it
          is legally defined as a lottery), promotions involving monetary
          rewards, including gift cards, or sweepstakes.
        </ListItem>
        <ListItem>
          <Strong>Hate, discrimination, and harassment:</Strong> content that we
          deem, in our sole discretion, to be in support of hate, violence,
          harassment, bullying, discrimination, terrorism, or intolerance of any
          kind relating to race, ethnicity, national origin, religious
          affiliation, sexual orientation, sex, gender or gender identity, or
          mental, physical, or other medical conditions.
        </ListItem>
        <ListItem>
          <Strong>Legal defense for serious crimes:</Strong> campaigns that we
          deem, in our sole discretion, to be for the legal defense of alleged
          crimes associated with hate, violence, harassment, bullying,
          discrimination, terrorism, or intolerance of any kind relating to
          race, ethnicity, national origin, religious affiliation, sexual
          orientation, sex, gender or gender identity, mental, physical, or
          other medical conditions, or financial crimes or crimes of deception.
        </ListItem>
        <ListItem>
          <Strong>International sanctions:</Strong> activities with, in, or
          involving countries, regions, governments, persons, or entities that
          are subject to U.S. and other economic sanctions under applicable law
          unless such activities are expressly authorized by the appropriate
          governmental authority.
        </ListItem>
        <ListItem>
          <Strong>Payment manipulation:</Strong> collecting payments on behalf
          of merchants by payment processors or otherwise; including, but not
          limited to, self-payments on campaigns or an attempt to bypass or
          otherwise circumvent the designated method of payment as provided by
          Company.
        </ListItem>
        <ListItem>
          <Strong>Misuse of funds:</Strong> the receipt or grant of cash
          advances or lines of credit to yourself or to another person for
          purposes other than those purposes clearly stated in the campaign or
          collecting or providing funds for any purpose other than as described
          in a campaign description.
        </ListItem>
        <ListItem>
          <Strong>Exploitation:</Strong> funding a ransom, human trafficking or
          exploitation, vigilantism, bribes or bounty.
        </ListItem>
        <ListItem>
          <Strong>Pornography:</Strong> pornographic content or other sexual
          content relating to minors or persons appearing to possibly be minors.
        </ListItem>
        <ListItem>
          <Strong>Graphic:</Strong> offensive, graphic, perverse or sensitive
          content.
        </ListItem>
        <ListItem>
          <Strong>Pre-sales:</Strong> the sale of items before the seller has
          control or possession of the item.
        </ListItem>
        <ListItem>
          <Strong>Credit:</Strong> credit repair or debt settlement services.
        </ListItem>
        <ListItem>
          <Strong>Slander and personal attacks:</Strong> publication or removal
          of User-Generated Content (such as mug shots), where the primary
          purpose of posting such User-Generated Content is to cause or raise
          concerns of reputational harm, as well as personal attacks, slurs,
          caustic or combative content submitted with the aim of harming other
          people in some way.
        </ListItem>
        <ListItem>
          <Strong>No-value-added services:</Strong> sale or resale of a service
          without added benefit to the buyer or resale of government offerings
          without authorization or added value.
        </ListItem>
        <ListItem>
          <Strong>Money laundering:</Strong> aggregation of funds owed to third
          parties, factoring, or other activities intended to obfuscate the
          origin of funds.
        </ListItem>
        <ListItem>
          <Strong>Piracy:</Strong> counterfeit music, movies, software, or other
          licensed materials without the appropriate authorization from the
          rights holder.
        </ListItem>
        <ListItem>
          <Strong>Intellectual property violations:</Strong> products or
          services that directly infringe or facilitate infringement upon the
          trademark, patent, copyright, trade secrets, or proprietary or privacy
          rights of any third party.
        </ListItem>
        <ListItem>
          <Strong>Unauthorized sale or resale:</Strong> of brand name or
          designer products or services.
        </ListItem>
        <ListItem>
          <Strong>Trade violations:</Strong> sale of goods or services that are
          illegally imported or exported.
        </ListItem>
        <ListItem>
          <Strong>Credit card fraud:</Strong> processing where there is no bona
          fide donation accepted; cash advance; card testing; or evasion of card
          network chargeback monitoring programs.
        </ListItem>
        <ListItem>
          <Strong>Financial crimes:</Strong> any other activity that Company may
          deem in its sole discretion to be in support of individuals and/or
          entities associated with alleged financial crimes, including, but not
          limited to, corruption, bribery, tax evasion, fraud, and activities of
          a similar nature; or
        </ListItem>
        <ListItem>
          <Strong>Generally unacceptable activity:</Strong> any other activity
          that Company may deem in its sole discretion to be unacceptable.
        </ListItem>
      </List>

      <SubTitle>9. DISPUTE RESOLUTION AND ARBITRATION</SubTitle>
      <Paragraph>
        <Strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL
        RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</Strong>
      </Paragraph>
      <Paragraph>
        <Strong>9.1 Binding Arbitration:</Strong> You and SkipGive agree that any
        dispute, claim, or controversy arising out of or relating to these Terms
        or the breach, termination, enforcement, interpretation, or validity
        thereof, or to the use of the Platform (collectively, "Disputes") will be
        settled by binding individual arbitration (the "Arbitration Agreement").
        If there is a dispute about whether this Arbitration Agreement can be
        enforced or applies to a Dispute, you and SkipGive agree that the
        arbitrator will decide that issue. Notwithstanding the foregoing, either
        party may bring an action in court to seek injunctive or other equitable
        relief in connection with claims related to intellectual property rights,
        proprietary information, or unauthorized access to the Platform.
      </Paragraph>
      <Paragraph>
        <Strong>9.2 No Class Actions:</Strong> YOU AND SKIPGIVE AGREE THAT EACH
        MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL
        CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
        REPRESENTATIVE PROCEEDING. Unless both you and SkipGive agree otherwise,
        the arbitrator may not consolidate more than one person's claims and may
        not otherwise preside over any form of a representative or class
        proceeding.
      </Paragraph>
      <Paragraph>
        <Strong>9.3 Arbitration Rules and Forum:</Strong> This Arbitration
        Agreement evidences a transaction in interstate commerce, and thus the
        Federal Arbitration Act governs the interpretation and enforcement of
        this provision. Arbitration will be initiated through and administered by
        JAMS pursuant to its Streamlined Arbitration Rules and Procedures
        (collectively, the "JAMS Rules"), as modified by this Arbitration
        Agreement. The JAMS Rules and instructions for how to initiate an
        arbitration are available at www.jamsadr.com or by calling JAMS at
        800-352-5267. The arbitration shall be conducted by a single, neutral
        arbitrator. Any claims or disputes where the total amount of the award
        sought is less than Ten Thousand U.S. Dollars ($10,000.00 USD) may be
        resolved through binding non-appearance-based arbitration, at the option
        of the party seeking relief. For claims or disputes where the total
        amount of the award sought is Ten Thousand U.S. Dollars ($10,000.00 USD)
        or more, the right to a hearing will be determined by the JAMS Rules.
      </Paragraph>
      <Paragraph>
        <Strong>9.4 Costs of Arbitration:</Strong> Payment of all filing,
        administration, and arbitrator fees will be governed by the JAMS Rules.
        If the arbitrator finds that you cannot afford to pay the JAMS filing,
        administrative, hearing, and/or other fees and cannot obtain a waiver
        from JAMS, SkipGive will pay them for you. In addition, SkipGive will
        reimburse all such JAMS filing, administrative, hearing, and/or other
        fees for claims totaling less than Ten Thousand U.S. Dollars ($10,000.00
        USD) unless the arbitrator determines the claims are frivolous.
      </Paragraph>
      <Paragraph>
        <Strong>9.5 Opt-Out:</Strong> You have the right to opt out of this
        Arbitration Agreement by sending written notice of your decision to opt
        out to hello@skipgive.com within thirty (30) days of first accepting
        these Terms. Your notice must include your name, mailing address, and
        email address. If you opt out of this Arbitration Agreement, all other
        parts of these Terms will continue to apply to you. Opting out of this
        Arbitration Agreement has no effect on any other arbitration agreements
        that you may currently have with SkipGive or may enter into in the
        future.
      </Paragraph>
      <Paragraph>
        <Strong>9.6 Severability:</Strong> If any portion of this Section 9 is
        found to be unenforceable or unlawful for any reason, (a) the
        unenforceable or unlawful provision shall be severed from these Terms;
        (b) severance of the unenforceable or unlawful provision shall have no
        impact whatsoever on the remainder of this Section 9 or the parties'
        ability to compel arbitration of any remaining claims on an individual
        basis pursuant to this Section 9; and (c) to the extent that any claims
        must therefore proceed on a class, collective, consolidated, or
        representative basis, such claims must be litigated in a civil court of
        competent jurisdiction and not in arbitration, and the parties agree that
        litigation of those claims shall be stayed pending the outcome of any
        individual claims in arbitration.
      </Paragraph>

      <SubTitle>10. MODIFICATIONS TO TERMS</SubTitle>
      <Paragraph>
        SkipGive reserves the right, at its sole discretion, to modify or replace
        these Terms at any time. We will provide notice of any material changes
        by posting the new Terms on the Platform and updating the "Effective
        Date" at the top of these Terms. We may also attempt to notify you via
        email or through a notice on the Platform. Your continued use of the
        Platform following the posting of revised Terms means that you accept and
        agree to the changes. You are expected to check this page frequently so
        you are aware of any changes, as they are binding on you. If you do not
        agree to the modified Terms, you must stop using the Platform.
      </Paragraph>

      <SubTitle>11. TERMINATION</SubTitle>
      <Paragraph>
        <Strong>11.1 Termination by SkipGive:</Strong> SkipGive reserves the
        right, in its sole discretion, to terminate, suspend, or restrict your
        access to all or any part of the Platform, at any time, for any reason,
        with or without prior notice, and without liability. SkipGive may
        terminate your account or your access immediately, without prior notice
        or liability, for any reason whatsoever, including but not limited to if
        you breach these Terms. Without limiting the foregoing, SkipGive reserves
        the right to terminate your access if you: (a) violate or breach any term
        of these Terms; (b) upon request by law enforcement or other government
        agencies; (c) discontinue or materially modify the Platform; (d) engage
        in unexpected technical issues or problems; or (e) engage in fraudulent
        or illegal activities.
      </Paragraph>
      <Paragraph>
        <Strong>11.2 Termination by You:</Strong> You may terminate your account
        at any time by contacting SkipGive at hello@skipgive.com. Upon
        termination, your right to use the Platform will immediately cease. If
        you wish to terminate your account, you may simply discontinue using the
        Platform.
      </Paragraph>
      <Paragraph>
        <Strong>11.3 Effect of Termination:</Strong> Upon termination of your
        access to or use of the Platform, all provisions of these Terms which by
        their nature should survive termination shall survive, including, without
        limitation, ownership provisions, warranty disclaimers, indemnification,
        limitations of liability, dispute resolution provisions, and general
        provisions.
      </Paragraph>

      <SubTitle>12. GOVERNING LAW AND VENUE</SubTitle>
      <Paragraph>
        These Terms and any action related thereto shall be governed by and
        construed in accordance with the laws of the State of Delaware, without
        regard to its conflict of laws provisions. Except as otherwise expressly
        set forth in Section 9 (Dispute Resolution and Arbitration), the
        exclusive jurisdiction and venue for any action with respect to the
        subject matter of these Terms shall be the state and federal courts
        located in Delaware, and each of the parties hereto waives any objection
        to jurisdiction and venue in such courts. YOU HEREBY WAIVE YOUR RIGHT TO
        A JURY TRIAL WITH RESPECT TO ANY CLAIM, ACTION, OR PROCEEDING, DIRECTLY
        OR INDIRECTLY, ARISING OUT OF OR RELATING TO THESE TERMS OR THE
        TRANSACTIONS CONTEMPLATED BY THESE TERMS TO THE FULLEST EXTENT PERMITTED
        BY LAW.
      </Paragraph>

      <SubTitle>13. FORCE MAJEURE</SubTitle>
      <Paragraph>
        SkipGive shall not be liable for any failure or delay in performance
        under these Terms due to causes beyond its reasonable control, including
        but not limited to acts of God, acts of war, terrorism, civil unrest,
        labor disputes, pandemics or epidemics, government actions or
        regulations, internet service provider failures or delays, cyberattacks,
        denial of service attacks, or any other causes beyond SkipGive's
        reasonable control. In such event, SkipGive's performance obligations
        shall be suspended for the duration of such force majeure event.
      </Paragraph>

      <SubTitle>14. INDEPENDENT CONTRACTORS; NO AGENCY</SubTitle>
      <Paragraph>
        The parties are independent contractors with respect to each other, and
        nothing in these Terms shall be construed as creating an
        employer-employee relationship, a partnership, or a joint venture between
        you and SkipGive. You have no authority to assume or create any
        obligations for or on behalf of SkipGive, express or implied, and you
        must not attempt to bind SkipGive to any contract.
      </Paragraph>

      <SubTitle>15. ENTIRE AGREEMENT AND SEVERABILITY</SubTitle>
      <Paragraph>
        <Strong>15.1 Entire Agreement:</Strong> These Terms, together with our
        Privacy Policy and SMS Text Policy, constitute the entire agreement
        between you and SkipGive regarding the use of the Platform and supersede
        all prior or contemporaneous understandings and agreements, whether
        written or oral, with respect to the Platform.
      </Paragraph>
      <Paragraph>
        <Strong>15.2 Severability:</Strong> If any provision of these Terms is
        held to be invalid, illegal, or unenforceable by a court of competent
        jurisdiction, such provision shall be limited or eliminated to the
        minimum extent necessary, and the remaining provisions of these Terms
        shall remain in full force and effect. The parties further agree that if
        any provision contained in these Terms is, to any extent, held invalid or
        unenforceable, the remainder of these Terms will not be affected, and
        each provision of these Terms will be valid and enforced to the fullest
        extent permitted by law.
      </Paragraph>

      <SubTitle>16. WAIVER AND ASSIGNMENT</SubTitle>
      <Paragraph>
        <Strong>16.1 Waiver:</Strong> No waiver of any term of these Terms shall
        be deemed a further or continuing waiver of such term or any other term,
        and SkipGive's failure to assert any right or provision under these Terms
        shall not constitute a waiver of such right or provision. Any waiver of
        any provision of these Terms will be effective only if in writing and
        signed by SkipGive.
      </Paragraph>
      <Paragraph>
        <Strong>16.2 Assignment:</Strong> You may not assign, transfer, or
        delegate these Terms or your rights and obligations hereunder, in whole
        or in part, whether voluntarily or by operation of law, without
        SkipGive's prior written consent. Any purported assignment, transfer, or
        delegation by you without SkipGive's prior written consent will be null
        and void. SkipGive may freely assign, transfer, or delegate these Terms
        or its rights and obligations hereunder, in whole or in part. Subject to
        the foregoing, these Terms will bind and inure to the benefit of the
        parties and their respective successors and permitted assigns.
      </Paragraph>

      <SubTitle>17. CONTACT INFORMATION</SubTitle>
      <Paragraph>
        If you have any questions, concerns, or complaints about these Terms or
        the Platform, please contact SkipGive at hello@skipgive.com. By using the
        Platform, you acknowledge that you have read, understood, and agree to be
        bound by these Terms of Service.
      </Paragraph>
    </Container>
  );
};
